import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            // 401 is the expected case for anonymous visitors — silence those only
            if (e?.response?.status !== 401) {
                console.warn("auth.checkAuth failed:", e?.message || e);
            }
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Skip /me check if returning from OAuth callback — AuthCallback handles it first
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("tk_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (email, password, name) => {
        const { data } = await api.post("/auth/register", { email, password, name });
        localStorage.setItem("tk_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // Logout endpoint failures don't block local sign-out; just log for diagnostics
            console.warn("auth.logout server call failed:", e?.message || e);
        }
        localStorage.removeItem("tk_token");
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, register, logout, refresh: checkAuth, setUser }),
        [user, loading, login, register, logout, checkAuth],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
